import React, { Fragment, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Menu } from "@headlessui/react";

import { EyeIcon } from "icons/payments/EyeIcon";
import { UpdatePlan } from "icons/dashboard/updatePlan";
import { CancleIcon } from "icons/dashboard/canclePlan";
import { MoreIcon } from "icons/payments/MoreIcon";
import PaymentMethodUpdateIcon from "icons/subscription/PaymentMethodUpdateIcon";
import ManageUpdateIcon from "icons/subscription/ManageUpdateIcon";
import { MoreIconActive, PlanNoDataIcon } from "utils/icon";
import {
  amountToFormat,
  changePaymentScheduleName,
  getPaymentMethod,
} from "utils/HelperFunctions";
import { formatPaymentListDate } from "utils/DateFormat";
import { PlanDetails } from "./DashboardPage";
import { isSettlementOriginPlan } from "utils/settlementPlan";
import { RootState } from "store/store";
import { SettingsValue } from "types/payments";
import {
  isCacelledBefore,
  isCancelsSoon,
  isEnded,
  isEndSoon,
} from "utils/Formater/DateFormat";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { useTid } from "utils/testid";

interface SubscriptionPaymentPlanSectionProps {
  subscriptionPaymentPlan: PlanDetails[] | undefined;
  isLoading: boolean;
  onPreviewClick: (id: number) => void;
  onUpdatePlanClick: (planDetails: PlanDetails) => void;
  onUpdatePaymentMethodClick: (planDetails: PlanDetails) => void;
  onScheduleUpdateClick: (id: number) => void;
  onCancelPlanClick: (planDetails: PlanDetails) => void;
  onDontCancelClick: (id: number) => void;
  userData: any;
}

const SubscriptionPaymentPlanSection: React.FC<
  SubscriptionPaymentPlanSectionProps
> = ({
  subscriptionPaymentPlan,
  isLoading,
  onPreviewClick,
  onUpdatePlanClick,
  onUpdatePaymentMethodClick,
  onScheduleUpdateClick,
  onCancelPlanClick,
  onDontCancelClick,
  userData,
}) => {
  const tid = useTid();
  const navigate = useNavigate();
  const { clusterId: cluster_id } = useParams();
  const [focusedIcons, setFocusedIcons] = useState<boolean[]>([]);
  const { accountPermission } = useSelector((state: RootState) => state.auth);

  const toggleFocusIcon = (index: number): void => {
    setFocusedIcons((prev: boolean[]): boolean[] => {
      const newFocusIcons = [...prev];
      newFocusIcons[index] = !newFocusIcons[index];
      return newFocusIcons;
    });
  };

  const getSubscriptionName = useMemo(() => {
    return (productDetails: any) => {
      if (productDetails?.length > 0) {
        const sortedProducts = [...productDetails].filter(
          (x: any) => x?.priceDetails?.frequency === "recurring"
        );

        let title = sortedProducts?.[0]?.productDetails?.name || "";
        if (sortedProducts?.length > 1) {
          return (
            title?.charAt(0) +
            title?.slice(1) +
            ` and ${sortedProducts.length - 1} more`
          );
        } else {
          return title?.charAt(0) + title?.slice(1);
        }
      } else {
        return "Custom Plan";
      }
    };
  }, []);

  const displayedPlans = useMemo(() => {
    return subscriptionPaymentPlan && Array.isArray(subscriptionPaymentPlan)
      ? subscriptionPaymentPlan.slice(0, 2)
      : [];
  }, [subscriptionPaymentPlan]);

  const hasPlans = useMemo(() => {
    return (
      Array.isArray(subscriptionPaymentPlan) &&
      subscriptionPaymentPlan.length > 0
    );
  }, [subscriptionPaymentPlan]);

  const calculatePlanDetails = useMemo(() => {
    return (planDetails: PlanDetails) => {
      const fee =
        planDetails?.recurring_type === "subscription"
          ? 0
          : (planDetails?.payment_method === "ACH"
              ? (planDetails?.ach_fee_amount ?? 0)
              : 0) +
            (planDetails?.payment_method !== "ACH"
              ? (planDetails?.cc_fee_amount ?? 0)
              : 0);

      let productAmount = 0;

      const filteredProducts = planDetails?.productSubscriptions?.filter(
        (product: any) => product?.priceDetails?.frequency === "recurring"
      );

      if (filteredProducts && filteredProducts.length > 0) {
        filteredProducts.forEach((product: any) => {
          productAmount += product?.amount * (product?.quantity || 1) || 0;
        });
      }

      const next_payment_amount =
        (planDetails?.next_payment_amount &&
        planDetails?.next_payment_amount > 0
          ? planDetails?.next_payment_amount
          : 0) + fee;

      const isCancelledAtPeriodEnd =
        planDetails?.recurring_type === "subscription" &&
        planDetails?.canceled_at !== null &&
        isCancelsSoon(planDetails?.canceled_at);

      const isEndsSoon = isEndSoon({
        date: planDetails?.end_sub,
        frequency: planDetails?.payment_schedule,
        intervalCount: filteredProducts?.[0]?.priceDetails?.interval_count || 1,
        intervalSelector:
          filteredProducts?.[0]?.priceDetails?.interval_selector || "day",
      });

      const isAlreadyEnd = isEnded(planDetails?.end_sub);

      const isCancelledBeforePeriodEnd = isCacelledBefore(
        planDetails?.canceled_at,
        planDetails?.end_sub
      );

      const isAgreement = planDetails?.plan_id && planDetails?.plan_id > 0;

      const isSettlementPlan = isSettlementOriginPlan(planDetails);

      const isUpdatePlan = isSettlementPlan
        ? false
        : (planDetails?.plan_id ?? 0) > 0 ||
            planDetails?.recurring_type === "schedule"
          ? accountPermission?.modify_plan === SettingsValue.YES && !isAgreement
          : filteredProducts?.length === 1 &&
            !planDetails?.downgrade_pending &&
            planDetails?.payment_collection_type === "MOF";

      const isUpdatePaymentMethod =
        (planDetails?.recurring_type === "subscription" &&
          planDetails?.sub_status === "ACTIVE") ||
        (planDetails?.recurring_type === "schedule" &&
          planDetails?.last_payment_status?.toLowerCase() !== "cancelled");

      const isDowngradeSchedule =
        planDetails?.recurring_type === "subscription" &&
        planDetails?.downgrade_pending &&
        planDetails?.sub_status !== "CANCELLED" &&
        planDetails?.sub_status !== "CANCELED";

      const date =
        (isEndsSoon || isAlreadyEnd) && !isCancelledBeforePeriodEnd
          ? planDetails?.end_sub
          : planDetails?.sub_status === "CANCELLED" || isCancelledAtPeriodEnd
            ? planDetails?.canceled_at || planDetails?.next_billing_date
            : planDetails?.next_billing_date;

      // A finished schedule has no next payment: showing the line anyway
      // rendered "Invalid date" and billed the convenience fee as the amount.
      const hasNextPayment =
        planDetails?.recurring_type === "subscription"
          ? true
          : Boolean(date) &&
            !Number.isNaN(new Date(date as string).getTime()) &&
            (planDetails?.next_payment_amount ?? 0) > 0;

      let status = "";
      if (planDetails?.recurring_type !== "subscription") {
        status = "Next payment on";
      } else {
        if ((isEndsSoon || isAlreadyEnd) && !isCancelledBeforePeriodEnd) {
          status = isAlreadyEnd ? "Ended on" : "Ends on";
        } else if (
          planDetails?.sub_status === "CANCELLED" ||
          isCancelledAtPeriodEnd
        ) {
          status = isCancelsSoon(planDetails.canceled_at)
            ? "Cancels on"
            : "Canceled on";
        } else {
          status = "Renews on";
        }
      }

      return {
        fee,
        status,
        next_payment_amount,
        isCancelledAtPeriodEnd,
        isUpdatePlan,
        isUpdatePaymentMethod,
        isDowngradeSchedule,
        isSettlementPlan,
        hasNextPayment,
        date,
        isAgreement,
      };
    };
  }, [accountPermission]);

  if (isLoading) {
    return (
      <div className="mt-12" data-testid={tid("subscriptions-plans-section")}>
        <div className="flex justify-between">
          <div className="text-xl text-txtPrimary font-medium">
            Subscriptions & Plans
          </div>
        </div>
        {[0, 1].map((key, index) => (
          <React.Fragment key={index}>
            <div className="mt-6">
              <p className="">
                <Skeleton height={36} width={200} />
              </p>
              <div className="flex gap-2 mt-2">
                <Skeleton width={500} height={16} />
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div
      className="mt-12"
      data-testid={tid("subscriptions-plans-section")}
      aria-label="Subscriptions and plans"
    >
      <div className="flex justify-between">
        <div className="text-xl text-txtPrimary font-medium">
          Subscriptions & Plans
        </div>
      </div>

      {hasPlans ? (
        <>
          {displayedPlans.map((planDetails: PlanDetails, index1: number) => {
            const isFocused = focusedIcons[index1] || false;
            const {
              next_payment_amount,
              isCancelledAtPeriodEnd,
              isUpdatePlan,
              isUpdatePaymentMethod,
              isDowngradeSchedule,
              isSettlementPlan,
              hasNextPayment,
              date,
              status,
              isAgreement,
            } = calculatePlanDetails(planDetails);

            const handlePreviewClick = () => {
              onPreviewClick(planDetails.id);
            };

            const handleUpdatePlanClick = () => {
              onUpdatePlanClick(planDetails);
            };

            const handleUpdatePaymentMethodClick = () => {
              onUpdatePaymentMethodClick(planDetails);
            };

            const handleScheduleUpdateClick = () => {
              onScheduleUpdateClick(planDetails.id);
            };

            const handleCancelPlanClick = () => {
              onCancelPlanClick(planDetails);
            };

            const handleDontCancelClick = () => {
              onDontCancelClick(planDetails.id);
            };

            return (
              <Fragment key={planDetails?.id ?? index1}>
                <div
                  className="w-full py-2 mt-4"
                  data-testid={tid(`plan-row-${planDetails?.id}`)}
                >
                  <div className="flex items-center justify-between w-full">
                    <p className="lg:text-2xl text-[22px] text-txtPrimary font-medium">
                      {planDetails?.plan_id && planDetails?.plan_id > 0
                        ? "Payment Agreement"
                        : planDetails?.recurring_type === "schedule"
                          ? "Custom Plan"
                          : getSubscriptionName(
                              planDetails?.productSubscriptions
                            )}
                    </p>
                    <Menu as="div" className="relative flex h-fit">
                      <Menu.Button
                        data-testid={tid(`plan-${planDetails?.id}-menu-button`)}
                        aria-label="Open plan actions"
                      >
                        {isFocused ? (
                          <MoreIconActive
                            onClick={() => toggleFocusIcon(index1)}
                          />
                        ) : (
                          <MoreIcon
                            className="group"
                            _className="group-hover:!fill-iconObscure"
                            onClick={() => toggleFocusIcon(index1)}
                          />
                        )}
                      </Menu.Button>

                      <Menu.Items
                        className={`absolute ${!isUpdatePaymentMethod && !isDowngradeSchedule ? "w-[153px] -left-[130px]" : "w-[225px] -left-[200px]"} top-[35px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg`}
                        data-testid={tid(`plan-${planDetails?.id}-menu`)}
                      >
                        <Menu.Item
                          as="div"
                          className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary font-normal leading-normal items-center cursor-pointer hover:!bg-bgSecondary"
                          data-testid={tid(
                            `plan-${planDetails?.id}-preview-button`
                          )}
                          aria-label="Preview plan"
                          onClick={handlePreviewClick}
                        >
                          <EyeIcon /> Preview
                        </Menu.Item>

                        {planDetails?.last_payment_status?.toLowerCase() !==
                          "cancelled" &&
                          planDetails?.last_payment_status?.toLowerCase() !==
                            "canceled" &&
                          !isCancelledAtPeriodEnd && (
                            <>
                              {isUpdatePlan && !isAgreement &&
                                planDetails?.last_payment_status !==
                                  "Completed" &&
                                planDetails.sub_status !== "CANCELLED" && (
                                  <Menu.Item
                                    as="div"
                                    className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary font-normal leading-normal items-center cursor-pointer hover:!bg-bgSecondary"
                                    data-testid={tid(
                                      `plan-${planDetails?.id}-update-plan-button`
                                    )}
                                    aria-label="Update plan"
                                    onClick={handleUpdatePlanClick}
                                  >
                                    <UpdatePlan /> Update plan
                                  </Menu.Item>
                                )}

                              {isUpdatePaymentMethod && !isAgreement && (
                                <Menu.Item
                                  as="div"
                                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                                  data-testid={tid(
                                    `plan-${planDetails?.id}-update-payment-method-button`
                                  )}
                                  aria-label="Update payment method"
                                  onClick={handleUpdatePaymentMethodClick}
                                >
                                  <PaymentMethodUpdateIcon /> Update payment
                                  method
                                </Menu.Item>
                              )}

                              {isDowngradeSchedule && (
                                <Menu.Item
                                  as="div"
                                  className="px-[15px] cursor-pointer py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                                  data-testid={tid(
                                    `plan-${planDetails?.id}-manage-scheduled-update-button`
                                  )}
                                  aria-label="Manage scheduled update"
                                  onClick={handleScheduleUpdateClick}
                                >
                                  <ManageUpdateIcon width="16" height="16" />{" "}
                                  Manage scheduled update
                                </Menu.Item>
                              )}

                              {accountPermission?.cancel_plan ===
                                SettingsValue.YES &&
                                !isSettlementPlan &&
                                !isAgreement &&
                                planDetails?.last_payment_status !==
                                  "Completed" &&
                                planDetails.sub_status !== "ENDED" &&
                                planDetails.sub_status !== "CANCELLED" && (
                                  <Menu.Item
                                    as="div"
                                    className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary font-normal leading-normal items-center cursor-pointer border-t-[1px] border-strokePrimary hover:!bg-bgSecondary"
                                    data-testid={tid(
                                      `plan-${planDetails?.id}-cancel-button`
                                    )}
                                    aria-label="Cancel plan"
                                    onClick={handleCancelPlanClick}
                                  >
                                    <CancleIcon /> Cancel plan
                                  </Menu.Item>
                                )}
                            </>
                          )}

                        {isCancelledAtPeriodEnd && (
                          <Menu.Item
                            as="div"
                            className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px] border-t border-strokePrimary"
                            data-testid={tid(
                              `plan-${planDetails?.id}-dont-cancel-button`
                            )}
                            aria-label="Do not cancel plan"
                            onClick={handleDontCancelClick}
                          >
                            <CancleIcon width="16" height="16" /> Don't cancel
                          </Menu.Item>
                        )}
                      </Menu.Items>
                    </Menu>
                  </div>
                  <div className="flex gap-2 lg:text-base text-sm text-txtSecondary font-normal items-center mt-2">
                    <div className="flex items-center gap-[2px] border-r-2 pr-2">
                      {getPaymentMethod({
                        card_type: planDetails.cc_type,
                        method: planDetails.payment_method,
                        digits: planDetails.cc_4digit,
                        account_number:
                          planDetails?.account_number || planDetails?.cc_4digit,
                        className: "!gap-[2px]",
                        textClassName: "!text-txtSecondary",
                        isHidden: false,
                        payment_collection_type:
                          planDetails?.payment_collection_type,
                        bank_name: planDetails?.bank_name,
                        rowId: planDetails?.id,
                      }) || "--"}
                    </div>
                    <p
                      className={
                        hasNextPayment || planDetails?.showIcon === "true"
                          ? "md:border-r-2 pr-2"
                          : ""
                      }
                    >
                      Billed{" "}
                      <span className="lowercase">
                        {changePaymentScheduleName(
                          planDetails.payment_schedule,
                          planDetails?.productSubscriptions,
                          planDetails?.recurring_type
                        )}
                      </span>
                    </p>
                    {hasNextPayment && (
                      <div className="md:block hidden">
                        <p>
                          {status}{" "}
                          <span>
                            {formatPaymentListDate(
                              date,
                              userData?.data?.accountData?.timezone,
                              "short"
                            )}
                          </span>{" "}
                          for{" "}
                          <span className="font-medium text-txtPrimary">
                            {"$" + amountToFormat(next_payment_amount)}
                          </span>
                        </p>
                      </div>
                    )}

                    {planDetails?.showIcon === "true" && (
                      <div className="md:block hidden">
                        <CustomTooltip
                          maxwidth="264px"
                          place="top"
                          id={`Schedule-custom-${planDetails.id}`}
                          content={`<div class="font-normal text-xs leading-[18px] w-[250px]" > ${planDetails?.recurring_type === "subscription" ? "There are unpaid invoice(s) for this subscription." : "One or more of your recent payments have failed. Click here to view plan and retry."}  </div>`}
                          stopPropagation={true}
                        >
                          <div
                            id={`Schedule-custom-${planDetails.id}`}
                            className="bg-lightRedBg text-red py-[3px] px-[6px] flex items-center justify-center gap-1 text-[10px] rounded-[4px]"
                            data-testid={tid(
                              `plan-${planDetails?.id}-amount-due-indicator`
                            )}
                          >
                            <InfoRedCircleIcon />
                            <span className="leading-none">Amount due</span>
                          </div>
                        </CustomTooltip>
                      </div>
                    )}
                  </div>
                  <div className="md:hidden flex flex-wrap items-center gap-1 text-sm text-txtSecondary font-normal mt-2">
                    {hasNextPayment && (
                      <span className="w-fit">
                        {status}{" "}
                        <span>
                          {formatPaymentListDate(
                            date,
                            userData?.data?.accountData?.timezone,
                            "short"
                          )}
                        </span>{" "}
                        for{" "}
                        <span className="font-medium text-txtPrimary">
                          {"$" + amountToFormat(next_payment_amount)}
                        </span>
                      </span>
                    )}
                    {planDetails?.showIcon === "true" && (
                      <div>
                        <CustomTooltip
                          maxwidth="264px"
                          place="top"
                          id={`Schedule-custom-${planDetails.id}`}
                          content={`<div class="font-normal text-xs leading-[18px] w-[250px]" > ${planDetails?.recurring_type === "subscription" ? "There are unpaid invoice(s) for this subscription." : "One or more of your recent payments have failed. Click here to view plan and retry."}  </div>`}
                          stopPropagation={true}
                        >
                          <div
                            id={`Schedule-custom-${planDetails.id}`}
                            className="bg-lightRedBg text-red py-[3px] px-[6px] flex items-center justify-center gap-1 text-[10px] rounded-[4px]"
                            data-testid={tid(
                              `plan-${planDetails?.id}-mobile-amount-due-indicator`
                            )}
                          >
                            <InfoRedCircleIcon />
                            <span className="leading-none">Amount due</span>
                          </div>
                        </CustomTooltip>
                      </div>
                    )}
                  </div>
                </div>
              </Fragment>
            );
          })}

          {hasPlans && (
            <p
              className="text-blue text-sm underline font-medium cursor-pointer hover:!text-btnPrimaryHover md:mt-6 mt-5 w-fit"
              role="link"
              tabIndex={0}
              data-testid={tid("subscriptions-plans-view-all-link")}
              aria-label="View all subscriptions and plans"
              onClick={() => navigate(`/${cluster_id}/subscriptions-plans`)}
            >
              View all
            </p>
          )}
        </>
      ) : (
        <div className="mt-[50px] mb-16 flex flex-col items-center gap-4">
          <PlanNoDataIcon aria-hidden="true" />
          <p className="text-center text-txtSecondary text-base">
            You currently don't have any active subscriptions or plans.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPaymentPlanSection;
